import { grey } from "@mui/material/colors";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  GKButton,
  GKTypography,
  Grid2,
  Show,
  Typography,
} from "components";
import { useMutation } from "hooks/useQueries";
import {
  AdvisorOnboardFormInterface,
  SelectedaBasketInterface,
} from "interfaces/OnboardingInterface";
import { Controller, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";

const AdvisorOnboardingEquityBasket = ({
  basketList: { data: basketList },
  setOpen,
}: any) => {
  const form = useFormContext<AdvisorOnboardFormInterface>();

  const userData = useAppSelector((state) => state?.user?.userData);

  // ================================================================================================================= clientData={addedClientData}====
  const { mutate: handleBasketFeePlan } = useMutation({
    mutationKey: ["HANDLE_CLIENT_EB_MUTATUION"],
    mutationFn: (values: AdvisorOnboardFormInterface) =>
      axiosInstance.post(`/client-basket/`, {
        client: values.id,
        basketId: values.basketObject.id,
        feePlan: values.selectedFeePlan.id,
      }),
    onSuccess: () => {
      refetchQuery(QueryKeys.GET_COMPLIANCE_BY_CLIENT_ID);
      refetchQuery(QueryKeys.GET_CLIENT_BY_ID);
      form.setValue("activeStep", form.getValues("activeStep") + 1);
    },
  });
  // ================================================================================================================= clientData={addedClientData}====
  const { mutate: handleClientSelfOnboarding } = useMutation({
    mutationKey: ["HANDLE_CLIENT_SEND_LINK_MUTATUION"],
    mutationFn: (values: AdvisorOnboardFormInterface) =>
      axiosInstance.post(`/client/self-onboarding/`, {
        basketId: values.basketObject.id,
        feePlanId: values.selectedFeePlan.id,
        partnerId: values.partner || userData?.partnerId,
        clientName: values.personalDetails.name,
        phone: values.phone,
        clientEmail: values.personalDetails.email,
        companyId: userData.companyId,
        skipAdvanceFee: values.skipAdvanceFee,
        clientId: values.clientId,
      }),
    onSuccess: () => {
      setOpen(false);
    },
  });

  // ================================================================================================================= clientData={addedClientData}====

  const handleSubmit = (value: any) => {
    if (value === "manual") {
      handleClientSelfOnboarding(form.getValues());
    } else {
      handleBasketFeePlan(form.getValues());
    }
  };
  // ================================================================================================================= clientData={addedClientData}====

  return (
    <form
      id="advisor-onboarding-equity-basket-form"
      onSubmit={form.handleSubmit(() => {
        if (!form.getValues("selectedFeePlan.id")) {
          toast.error("Please select a fee plan");
        } else {
          handleSubmit(form.getValues("type"));
        }
      })}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={5.8} container spacing={1} alignContent={"flex-start"}>
          <Grid2 size={12}>
            <Typography
              fontSize={18}
              fontWeight={600}
              textAlign={"left"}
              width={"100%"}
            >
              Select Basket
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            container
            spacing={1}
            sx={{
              overflow: "auto",
              maxHeight: "calc(100vh - 446px)",
            }}
          >
            {basketList?.map((dataItem: SelectedaBasketInterface) => {
              return (
                <Grid2 size={12}>
                  <GKButton
                    key={dataItem.id}
                    fullWidth
                    size="large"
                    variant="outlined"
                    color={
                      dataItem?.id === form.getValues("basketObject.id")
                        ? "primary"
                        : "dark"
                    }
                    onClick={() => {
                      form.setValue("basketObject", dataItem);
                      form.setValue("selectedFeePlan", null);
                    }}
                  >
                    <Grid2 container size={12} py={2}>
                      <Grid2 size={12}>
                        <GKTypography
                          fontSize={18}
                          fontWeight={600}
                          textAlign={"left"}
                          color={
                            dataItem?.id === form.getValues("basketObject.id")
                              ? "var(--primary-900)"
                              : "var(--black)"
                          }
                        >
                          {dataItem.name}
                        </GKTypography>
                      </Grid2>
                      <Grid2 size={12}>
                        <GKTypography
                          fontSize={12}
                          textAlign={"left"}
                          dangerouslySetInnerHTML={{
                            __html: dataItem.overview,
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </GKButton>
                </Grid2>
              );
            })}
          </Grid2>
        </Grid2>
        <Grid2 size={0.2} textAlign={"center"}>
          <Divider orientation="vertical" />
        </Grid2>
        <Show.When
          isTrue={form.getValues("basketObject.feePlansList")?.length !== 0}
        >
          <Grid2 container size={5.8} alignContent={"flex-start"} spacing={1}>
            <Grid2 size={12}>
              <Typography
                fontSize={18}
                fontWeight={600}
                textAlign={"left"}
                width={"100%"}
              >
                Select Fee Plan
              </Typography>
              <Typography
                fontWeight={400}
                fontSize={12}
                textAlign={"left"}
                width={"100%"}
                color={grey[500]}
              >
                This fee is applicable for subscribing to the selected basket.
              </Typography>
            </Grid2>
            <Grid2 size={12} spacing={1} container>
              {form
                .getValues("basketObject.feePlansList")
                ?.filter((d) => d?.managementFee?.fee)
                ?.map((dataItem, index) => {
                  return (
                    <Grid2 size={{ xs: 12, md: 6 }} key={index}>
                      <GKButton
                        fullWidth
                        key={index}
                        variant={"outlined"}
                        color={
                          form.getValues("selectedFeePlan.id") === dataItem?.id
                            ? "primary"
                            : "dark"
                        }
                        onClick={() =>
                          form.setValue("selectedFeePlan", dataItem)
                        }
                        sx={{
                          p: "12px",
                          minHeight: "78px",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Grid2 container spacing={2}>
                          <Grid2 size={12} textAlign={"start"}>
                            <Typography fontSize={16} fontWeight={600}>
                              {dataItem?.name}
                            </Typography>
                          </Grid2>
                          <Grid2 size={6}>
                            {dataItem?.managementFee?.fee && (
                              <Box>
                                <Typography
                                  textTransform={"capitalize"}
                                  color={"var(--neutral-500)"}
                                  fontSize={12}
                                  textAlign={"left"}
                                >
                                  {dataItem?.managementFee?.name || "-"}
                                </Typography>
                                <GKTypography textAlign={"left"}>
                                  {dataItem?.managementFee?.feeType ===
                                  "Flat Fee"
                                    ? numberToCurrency(
                                        dataItem?.managementFee?.fee,
                                      )
                                    : `${dataItem?.managementFee?.fee}%` || "-"}
                                </GKTypography>
                              </Box>
                            )}
                          </Grid2>
                          <Grid2 size={6}>
                            {dataItem?.performanceFee?.fee && (
                              <Box>
                                <Typography
                                  textTransform={"capitalize"}
                                  color={"var(--neutral-500)"}
                                  textAlign={"left"}
                                  fontSize={12}
                                >
                                  {dataItem?.performanceFee?.name ||
                                    "Performance Fee"}
                                </Typography>
                                <GKTypography textAlign={"left"}>
                                  {`${dataItem?.managementFee?.fee}%` || "-"}
                                </GKTypography>
                              </Box>
                            )}
                          </Grid2>
                        </Grid2>
                      </GKButton>
                    </Grid2>
                  );
                })}
            </Grid2>
          </Grid2>
        </Show.When>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="skipAdvanceFee"
            control={form.control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Skip Advance Fee"
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
    </form>
  );
};

export default AdvisorOnboardingEquityBasket;
